// LP SDK entry: wires together identity + API + DOM swap.
// Exposes window.allbrainer = { identify, assign, track } and auto-inits
// from a <script data-project="..." data-base-url="..."> attribute.

import { getOrCreateVisitorId } from './identity';
import { fetchAssignments, postConvert } from './api';
import { installAntiFlicker, applySwaps } from './swap';
import {
  parseAttributionParams,
  storeAttribution,
  getStoredAttribution,
  getStoredSessionId,
  storeSessionId,
  generateSessionId,
} from './utils';
import { installCommerceMode } from './commerce';
import pkg from '../package.json';

export interface AllBrainerLP {
  identify(userId: string): void;
  assign(options?: { page?: string }): Promise<void>;
  track(event: string, opts?: { value?: number }): Promise<void>;
  VERSION: string;
}

declare global {
  interface Window {
    allbrainer: AllBrainerLP;
  }
}

let _project = '';
let _baseUrl = '';
let _visitorId: string | null = null;

export function identify(userId: string): void {
  _visitorId = userId;
}

// Canonicalises a path/slug to the bare form the API stores in lp_experiments.page_slug
// (`pricing`, `home` — see the dashboard's ExperimentForm placeholder and the assign-route
// tests). The root path is the one exception: it maps to '/' rather than '' because the
// server filter is `!page || e.pageSlug === page`, so an empty value is read as "no filter"
// and would hand the homepage every experiment in the project — the exact bug this fixes.
export function normalizePage(raw: string): string {
  const trimmed = raw.trim();
  const bare = trimmed.replace(/^\/+/, '').replace(/\/+$/, '');
  return bare || '/';
}

function currentPath(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

export async function assign(options?: { page?: string }): Promise<void> {
  if (!_project || !_visitorId) return;
  const override = typeof options === 'object' ? options?.page : undefined;
  const page = normalizePage(override?.trim() ? override : currentPath());
  const cleanup = installAntiFlicker();
  try {
    const assignments = await fetchAssignments({
      baseUrl: _baseUrl,
      project: _project,
      visitor: _visitorId,
      page,
    });
    applySwaps(assignments);
  } catch {
    // On fetch error, remove anti-flicker so page is never stuck hidden.
    cleanup();
  }
}

export async function track(event: string, opts?: { value?: number }): Promise<void> {
  if (!_project || !_visitorId) return;
  await postConvert({
    baseUrl: _baseUrl,
    project: _project,
    visitor: _visitorId,
    event,
    ...(opts?.value !== undefined && { value: opts.value }),
  });
}

// For testing: reset module state between suites.
export function _resetState(
  project = '',
  baseUrl = '',
  visitorId: string | null = null,
): void {
  _project = project;
  _baseUrl = baseUrl;
  _visitorId = visitorId;
}

export function autoInit(): void {
  const script = document.querySelector<HTMLScriptElement>('script[data-project]');
  if (!script) return;
  _project = script.getAttribute('data-project') ?? '';
  _baseUrl = script.getAttribute('data-base-url') ?? 'https://api.allbrainer.com';
  if (!_project) return;
  _visitorId = getOrCreateVisitorId();
  void assign();

  // Capture attribution (UTMs, click IDs incl. ttclid, ab_project/ab_variant)
  // and sync it onto the Shopify cart in commerce mode, so it survives into
  // checkout as note_attributes for the orders/paid webhook.
  const currentAttribution = parseAttributionParams();
  if (Object.keys(currentAttribution).length > 0) {
    storeAttribution(currentAttribution);
  }
  let sessionId = getStoredSessionId();
  if (!sessionId) {
    sessionId = generateSessionId();
    storeSessionId(sessionId);
  }
  if (_visitorId) {
    installCommerceMode({
      visitorId: _visitorId,
      sessionId,
      getAttribution: getStoredAttribution,
      track: (event, properties) => {
        const value = typeof properties?.price === 'number' ? properties.price : undefined;
        void track(event, value !== undefined ? { value } : undefined);
      },
      debug: script.hasAttribute('data-debug'),
    });
  }
}

export const allbrainer: AllBrainerLP = { identify, assign, track, VERSION: pkg.version };

if (typeof window !== 'undefined') {
  window.allbrainer = allbrainer;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}
